import { useState, useEffect } from 'react';
import api from '../api';
import SearchSelect from './SearchSelect';
import Combobox from './Combobox';
import { ContractorModal } from '../pages/Contractors';
import AllocationEditor, { cents } from './AllocationEditor';
import MonthSelect from './MonthSelect';

const PAY_TYPES = ['По реквизитам/счету', 'По номеру телефона', 'Самозанятому'];
const OUT_OF_PROJECT = 'ВНЕ ПРОЕКТА';

export default function PaymentForm({ onClose, onSaved, initial }) {
  const isEdit = !!initial;
  const [meta, setMeta] = useState(null);
  const [docType, setDocType] = useState(initial?.doc_type || 'request');
  const [payType, setPayType] = useState(initial?.pay_type || PAY_TYPES[0]);
  const [recipientQuery, setRecipientQuery] = useState(initial && !initial.contractor_id ? (initial.recipient || '') : '');
  const [contractor, setContractor] = useState(initial?.contractor_id ? { id: initial.contractor_id, raw: { name: initial.contractor_name || initial.recipient } } : null);
  const [direction, setDirection] = useState(initial?.direction || '');
  const [article, setArticle] = useState(initial?.article || '');
  const [project, setProject] = useState(initial?.project || '');
  const [split,setSplit]=useState(!!initial?.allocations?.length);
  const [allocations,setAllocations]=useState(initial?.allocations||[{amount:'',article:'',direction:'',project:''}]);
  const [amount, setAmount] = useState(initial?.amount ?? '');
  const [source, setSource] = useState(initial?.source || '');
  const [dateRequested, setDateRequested] = useState(initial?.date_requested?.slice(0, 10) || '');
  // Фактическая дата оплаты — раньше проставлялась только автоматически (NOW() в момент клика
  // «Оплачена»), которая часто не совпадает с реальной датой перевода денег (бухгалтер мог
  // отметить на день-два позже). Плюс раньше это поле тихо затиралось в null при любом
  // редактировании заявки, если её тип не «Оплата факт», — отдельное состояние фиксит обе беды.
  const [datePaidValue, setDatePaidValue] = useState(initial?.date_paid?.slice(0, 10) || '');
  const [comment, setComment] = useState(initial?.comment || '');
  const [requisitesText, setRequisitesText] = useState(initial?.contractor_requisites || '');
  const [requisitesFile, setRequisitesFile] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(initial?.phone_number || '');
  const [phoneRecipient, setPhoneRecipient] = useState(initial?.phone_recipient || '');
  const [phoneBank, setPhoneBank] = useState(initial?.phone_bank || '');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showNewContractor, setShowNewContractor] = useState(false);
  const [periodMode, setPeriodMode] = useState(initial?.reporting_period_from && initial?.reporting_period_to && initial.reporting_period_from !== initial.reporting_period_to ? 'range' : 'single');
  const [periodMonth, setPeriodMonth] = useState(initial?.reporting_period_from?.slice(0, 7) || '');
  const [periodFrom, setPeriodFrom] = useState(initial?.reporting_period_from?.slice(0, 7) || '');
  const [periodTo, setPeriodTo] = useState(initial?.reporting_period_to?.slice(0, 7) || '');

  useEffect(() => {
    api.get('/payments/meta').then(r => setMeta(r.data));
  }, []);

  const articlesForDirection = (meta?.articles || []).filter(a => {
    const dir = meta.directions.find(d => d.id === a.direction_id);
    return dir?.name === direction;
  });

  const searchContractors = async (q) => {
    const { data } = await api.get('/contractors/search', { params: { q } });
    return data.map(c => ({ id: c.id, label: `${c.name}${c.inn ? ' · ИНН ' + c.inn : ''}`, raw: c }));
  };

  const isOutOfProject = project.trim() === OUT_OF_PROJECT;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const isFact = docType === 'fact';
    if (!amount || (!split && (!direction || !article))) {
      setError('Заполните обязательные поля');
      return;
    }
    // «Оплата факт» — это уже случившийся платёж, задним числом; для него важны только сумма,
    // направление/статья, назначение платежа и подтверждение оплаты — проект/источник/тип
    // оплаты/реквизиты необязательны (в отличие от «Заявки на оплату», где это ещё предстоит).
    if (!isFact && (!project || !source)) {
      setError('Укажите проект и источник платежа');
      return;
    }
    const isPhonePay = payType === 'По номеру телефона';
    if (!isEdit) {
      if (!isFact && isPhonePay && (!phoneNumber || !phoneRecipient || !phoneBank)) {
        setError('Укажите номер телефона, получателя и банк перевода');
        return;
      }
      // Если получатель выбран из списка контрагентов — его реквизиты уже есть в системе,
      // повторно вписывать их вручную не нужно (иначе это дублирование одних и тех же данных).
      if (!isFact && !isPhonePay && !contractor && !requisitesText && !requisitesFile) {
        setError('Укажите реквизиты для оплаты — текстом или файлом (или выберите контрагента из списка, у которого они уже есть)');
        return;
      }
      if (isFact) {
        if (!comment.trim()) { setError('Укажите комментарий с назначением платежа'); return; }
        if (!files.length) { setError('Приложите чек или скриншот оплаты'); return; }
      }
    }
    let reportingPeriodFrom = null, reportingPeriodTo = null;
    if (isOutOfProject) {
      if (periodMode === 'single') {
        if (!periodMonth) { setError('Укажите месяц отчётного периода'); return; }
        reportingPeriodFrom = `${periodMonth}-01`;
        reportingPeriodTo = reportingPeriodFrom;
      } else {
        if (!periodFrom || !periodTo) { setError('Укажите начало и конец периода'); return; }
        if (periodFrom > periodTo) { setError('Начало периода должно быть раньше конца'); return; }
        reportingPeriodFrom = `${periodFrom}-01`;
        reportingPeriodTo = `${periodTo}-01`;
      }
    }
    if(split){
      if(allocations.some(r=>!r.article||!r.direction||!cents(r.amount))){setError('Заполните статьи и положительные суммы всех строк');return;}
      if((isFact || (isEdit && initial.status!=='new')) && allocations.reduce((n,r)=>n+cents(r.amount),0)!==cents(amount)){setError('Распределите всю сумму заявки');return;}
    }
    setSaving(true);
    try {
      let attachments = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post(`/upload?type=${docType}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        attachments.push({ url: data.url, filename: data.filename, kind: 'invoice' });
      }
      if (!isPhonePay && requisitesFile) {
        const fd = new FormData();
        fd.append('file', requisitesFile);
        const { data } = await api.post('/upload?type=requisites', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        attachments.push({ url: data.url, filename: data.filename, kind: 'requisites' });
      }
      const payload = {
        doc_type: docType,
        pay_type: payType,
        recipient: contractor?.raw?.name || recipientQuery || null,
        direction:split?allocations[0].direction:direction, article:split?allocations[0].article:article, project,
        allocations:split?allocations.map(r=>({...r,project:r.project||project})):[],
        amount: parseFloat(amount),
        source,
        date_requested: docType === 'request' ? (dateRequested || null) : null,
        // При создании новой заявки (не «факт») оплаты ещё не было — дата не отправляется.
        // При редактировании — отправляем то, что реально стоит в поле (в т.ч. пусто → null),
        // чтобы можно было проставить/поправить дату оплаты уже после создания заявки.
        date_paid: docType === 'fact' ? (datePaidValue || null) : (isEdit ? (datePaidValue || null) : null),
        comment: comment || null,
        contractor_id: contractor?.id || null,
        contractor_name: contractor?.raw?.name || null,
        contractor_requisites: !isPhonePay ? (requisitesText || null) : null,
        phone_number: isPhonePay ? phoneNumber : null,
        phone_recipient: isPhonePay ? phoneRecipient : null,
        phone_bank: isPhonePay ? phoneBank : null,
        reporting_period_from: reportingPeriodFrom,
        reporting_period_to: reportingPeriodTo
      };
      if (isEdit) {
        await api.patch(`/payments/${initial.id}`, payload);
      } else {
        await api.post('/payments', { ...payload, attachments });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  if (!meta) return (
    <div className="modal-overlay"><div className="modal">Загрузка справочников...</div></div>
  );

  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{isEdit ? `Редактирование заявки #${initial.id}` : 'Новая заявка'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={submit}>
          <div className="form-row">
            <div className="form-group">
              <label>Тип документа</label>
              <select value={docType} onChange={e => setDocType(e.target.value)}>
                <option value="request">Заявка на оплату</option>
                <option value="fact">Оплата факт</option>
              </select>
            </div>
            <div className="form-group">
              <label>Тип оплаты</label>
              <select value={payType} onChange={e => setPayType(e.target.value)}>
                {PAY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {payType === 'По номеру телефона' ? (
            <div className="form-row">
              <div className="form-group">
                <label>Номер телефона *</label>
                <input value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} required={!isEdit} />
              </div>
              <div className="form-group">
                <label>Получатель перевода *</label>
                <input value={phoneRecipient} onChange={e => setPhoneRecipient(e.target.value)} required={!isEdit} />
              </div>
              <div className="form-group">
                <label>Банк *</label>
                <input value={phoneBank} onChange={e => setPhoneBank(e.target.value)} required={!isEdit} />
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label>Реквизиты для оплаты{payType === 'Самозанятому' ? ' (самозанятый)' : ''}{contractor ? '' : ' *'}</label>
              <textarea rows={2} value={requisitesText} onChange={e => setRequisitesText(e.target.value)} placeholder={contractor ? 'Не обязательно — у контрагента уже есть реквизиты' : 'Текстом — или прикрепи файл ниже'} />
              <input type="file" accept="image/*,application/pdf,.xlsx,.xls" onChange={e => setRequisitesFile(e.target.files[0] || null)} style={{ marginTop: 6 }} />
              {contractor && (
                <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                  Выбран контрагент «{contractor.raw.name}» — его реквизиты уже есть в системе, заполнять это поле не обязательно.
                </div>
              )}
              {payType === 'Самозанятому' && !contractor && (
                <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                  Если этого самозанятого ещё нет в списке контрагентов — заведи его кнопкой «+ Новый» ниже с типом «Самозанятый»: для него потребуется справка о постановке на учёт.
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label>Получатель / контрагент</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <SearchSelect
                  value={contractor ? contractor.raw.name : recipientQuery}
                  onChange={(v) => { setRecipientQuery(v); setContractor(null); }}
                  onSelect={(opt) => { setContractor(opt); setRecipientQuery(opt.raw.name); }}
                  fetchOptions={searchContractors}
                  placeholder="Начните вводить название или ИНН"
                />
              </div>
              <button type="button" className="btn-secondary btn-sm" onClick={() => setShowNewContractor(true)}>+ Новый</button>
            </div>
          </div>

          <label><input type="checkbox" checked={split} onChange={e=>setSplit(e.target.checked)}/> Разбить сумму по статьям</label>
          {!split && <div className="form-row">
            <div className="form-group">
              <label>Направление *</label>
              <select value={direction} onChange={e => { setDirection(e.target.value); setArticle(''); }} required>
                <option value="">Выберите</option>
                {meta.directions.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Статья *</label>
              <Combobox
                value={article}
                onChange={setArticle}
                options={articlesForDirection.map(a => ({ value: a.name, label: a.name }))}
                placeholder={direction ? 'Начните вводить название статьи' : 'Сначала выберите направление'}
                disabled={!direction}
              />
            </div>
          </div>}

          {split && <AllocationEditor rows={allocations} onChange={setAllocations} meta={meta} total={amount} project={project}/>}
          <div className="form-row">
            <div className="form-group">
              <label>Проект{docType === 'fact' ? '' : ' *'}</label>
              <input list="projects-list" value={project} onChange={e => setProject(e.target.value)} required={docType !== 'fact'} />
              <datalist id="projects-list">
                {meta.projects.map(p => <option key={p} value={p} />)}
              </datalist>
            </div>
            <div className="form-group">
              <label>Источник{docType === 'fact' ? '' : ' *'}</label>
              <select value={source} onChange={e => setSource(e.target.value)} required={docType !== 'fact'}>
                <option value="">Выберите</option>
                {meta.sources.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
            </div>
          </div>

          {isOutOfProject && (
            <div className="form-group" style={{ background: '#f5f5f5', padding: 10, borderRadius: 6 }}>
              <label>Отчётный период (за какой месяц/срок платёж)</label>
              <div className="form-row">
                <select value={periodMode} onChange={e => setPeriodMode(e.target.value)}>
                  <option value="single">Конкретный месяц (например, телефония)</option>
                  <option value="range">Период на несколько месяцев (например, сервер/домен на год)</option>
                </select>
              </div>
              {periodMode === 'single' ? (
                <MonthSelect value={periodMonth} onChange={setPeriodMonth} />
              ) : (
                <div className="form-row">
                  <div className="form-group">
                    <label>С</label>
                    <MonthSelect value={periodFrom} onChange={setPeriodFrom} />
                  </div>
                  <div className="form-group">
                    <label>По</label>
                    <MonthSelect value={periodTo} onChange={setPeriodTo} />
                  </div>
                </div>
              )}
              {periodMode === 'range' && (
                <div className="text-muted" style={{ fontSize: 12 }}>Сумма будет поровну распределена по месяцам периода и корректно учтена в ОПиУ каждого месяца.</div>
              )}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Сумма, ₽ *</label>
              <input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>{docType === 'fact' ? 'Дата оплаты' : 'Оплатить до'}</label>
              <input
                type="date"
                value={docType === 'fact' ? datePaidValue : dateRequested}
                onChange={e => (docType === 'fact' ? setDatePaidValue : setDateRequested)(e.target.value)}
              />
            </div>
          </div>

          {isEdit && docType === 'request' && (
            <div className="form-group">
              <label>Дата фактической оплаты</label>
              <input type="date" value={datePaidValue} onChange={e => setDatePaidValue(e.target.value)} />
            </div>
          )}

          <div className="form-group">
            <label>Комментарий{docType === 'fact' ? ' (назначение платежа) *' : ''}</label>
            <textarea rows={3} value={comment} onChange={e => setComment(e.target.value)} required={docType === 'fact' && !isEdit} />
          </div>

          {!isEdit && (
            <div className="form-group">
              <label>{docType === 'fact' ? 'Чеки и скриншоты оплаты *' : 'Чеки, скриншоты и документы'} (изображения или PDF)</label>
              <input type="file" accept="image/*,application/pdf" multiple onChange={e => { const added=Array.from(e.target.files||[]); setFiles(prev=>[...prev,...added.filter(f=>!prev.some(p=>p.name===f.name&&p.size===f.size&&p.lastModified===f.lastModified))]); e.target.value=''; }} />
              <small>Можно выбрать несколько файлов сразу или добавить их по очереди. Файлы относятся ко всей заявке, включая разбивку.</small>
              {files.length>0 && <ul>{files.map((f,i)=><li key={`${f.name}-${f.size}-${f.lastModified}`}>{f.name} <button type="button" disabled={saving} onClick={()=>setFiles(prev=>prev.filter((_,j)=>j!==i))}>Убрать</button></li>)}</ul>}
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Сохранение...' : (isEdit ? 'Сохранить' : 'Создать')}</button>
          </div>
        </form>
      </div>
      {showNewContractor && (
        <ContractorModal
          onClose={() => setShowNewContractor(false)}
          onSaved={(saved) => {
            setShowNewContractor(false);
            if (saved) { setContractor({ id: saved.id, raw: saved }); setRecipientQuery(saved.name); }
          }}
        />
      )}
    </div>
  );
}
